import { CloseIconMd } from '@chownow/pantry/icons';

import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import type { Channel } from 'pusher-js';

import { ActionIcon, Button, LoadingOverlay, Modal, Stack, Text } from '@mantine/core';

import { logException } from 'helpers/loggers';
import { runMenuFixer } from 'helpers/menuManagement';

import { usePusher } from 'hooks/usePusher';

import { MENU_FIXER_COMPLETE } from 'constants/menuManagement';

const LOADING_MESSAGE = 'Initiating image fix process...';
const AWAITING_MESSAGE = 'Fixing images. This may take several minutes...';
const FIX_IMAGES_MESSAGE =
  'Some images in this menu contain URLs which are not hosted by ChowNow. We can attempt to download and host these images for you. The process may take several minutes.';
const FIX_IMAGES_FAILED_MESSAGE =
  'Some images failed to convert. Manually upload your images to the menu editor or contact customer support.';

type Menu = {
  merchantId: number | string;
  companyId: number | string;
  uuid: string;
};

type FixerResult = {
  menu_uuids: string[];
  num_successful: number;
  num_failed: number;
  failed_names?: string[];
};

type FixImageUrlsModalProps = {
  menu: Menu;
  opened: boolean;
  onClose: () => void;
};

export const FixImageUrlsModal: FunctionComponent<FixImageUrlsModalProps> = ({
  menu,
  opened,
  onClose,
}) => {
  const { channels } = usePusher();
  const { merchantId, companyId, uuid } = menu;
  const [isLoading, setIsLoading] = useState(false);
  const [isAwaitingFix, setIsAwaitingFix] = useState(false);
  const [fixerResult, setFixerResult] = useState<FixerResult | null>(null);
  const [showAlert, setShowAlert] = useState(true);

  const channel =
    companyId &&
    channels?.find(
      (ch: Channel) => ch.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${companyId}`,
    );

  useEffect(() => {
    if (!channel) return undefined;
    channel.bind(MENU_FIXER_COMPLETE, (event: FixerResult) => {
      if (event?.menu_uuids.includes(uuid)) {
        setIsAwaitingFix(false);
        setIsLoading(false);
        setFixerResult(event);
      }
    });

    return () => {
      channel.unbind(MENU_FIXER_COMPLETE);
    };
  }, [channel, uuid]);

  const requestMenuFixer = async () => {
    try {
      setIsLoading(true);
      await runMenuFixer({ uuid, merchantId, companyId });
      setIsAwaitingFix(true);
    } catch (e) {
      logException({
        error: e,
        message: 'Failed to run menu fixer',
        context: { uuid, merchantId, companyId },
        tags: {},
      });
      setIsLoading(false);
      setIsAwaitingFix(false);
    }
  };

  const handleClose = () => {
    setIsLoading(false);
    setIsAwaitingFix(false);
    setFixerResult(null);
    setShowAlert(true);
    onClose();
  };

  const handleDismissAlert = () => {
    setShowAlert(false);
  };

  const createFixerResult = () => {
    if (!fixerResult) return null;

    const numSuccessful = fixerResult.num_successful;
    const numFailed = fixerResult.num_failed;
    const failedNames = fixerResult.failed_names;

    if (!numFailed) {
      return (
        <>
          <Text>
            We&apos;ve successfully fixed {numSuccessful} image{numSuccessful !== 1 ? 's' : ''}.
          </Text>
          <Button onClick={handleClose} fullWidth>
            OK
          </Button>
        </>
      );
    }

    return (
      <>
        {showAlert && (
          <div
            style={{
              backgroundColor: 'var(--surface-caution)',
              borderRadius: '8px',
              padding: 'var(--dimension-xs)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--dimension-xs)',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#ff6b6b',
                flexShrink: 0,
              }}
            />
            <Text style={{ flex: 1 }}>
              We fixed {numSuccessful || 0} image{(numSuccessful || 0) !== 1 ? 's' : ''} and failed
              to fix {numFailed} image{numFailed !== 1 ? 's' : ''}
              {failedNames?.length && failedNames.length > 0 && ` on '${failedNames.join(', ')}'`}
            </Text>
            <ActionIcon
              onClick={handleDismissAlert}
              size="xs"
              variant="transparent"
              style={{ flexShrink: 0 }}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloseIconMd />
              </div>
            </ActionIcon>
          </div>
        )}
        <Text>{FIX_IMAGES_FAILED_MESSAGE}</Text>
        <Button onClick={handleClose} fullWidth>
          OK
        </Button>
      </>
    );
  };

  const selectContent = () => {
    if (isLoading || isAwaitingFix) {
      return (
        <>
          <LoadingOverlay visible zIndex={1000} />
          <Text>{isLoading ? LOADING_MESSAGE : AWAITING_MESSAGE}</Text>
        </>
      );
    }

    if (fixerResult) {
      return createFixerResult();
    }

    return (
      <>
        <Text>{FIX_IMAGES_MESSAGE}</Text>
        <Stack gap="xs">
          <Button
            onClick={() => {
              void requestMenuFixer();
            }}
            loading={isLoading}
            fullWidth
          >
            RESUME
          </Button>
          <Button variant="default" onClick={handleClose} fullWidth>
            CANCEL
          </Button>
        </Stack>
      </>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      data-testid="fixImageUrlsModal"
      size="md"
      centered
    >
      <Stack pt="xl">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Text variant="body-lg">FIX IMAGE URLS</Text>
          <ActionIcon onClick={handleClose}>
            <CloseIconMd />
          </ActionIcon>
        </div>
        {selectContent()}
      </Stack>
    </Modal>
  );
};
